import { z } from 'zod';

import { ExerciseCategory, ExerciseExample, HintBlock } from './exercise.js';
import { UiLanguage } from './languages.js';

/**
 * Classes, class live sessions, homework and teacher-authored exercises
 * (docs/accounts-classes-plan.md, ADR 0016/0017).
 */

/** 8 characters from the read-aloud alphabet (no 0/O, 1/I); session codes have 6. */
export const CLASS_CODE = /^[A-Z0-9]{8}$/;

export const ClassName = z.string().trim().min(1).max(60);

export const ClassSummary = z.object({
  id: z.string(),
  name: z.string(),
  teacherName: z.string(),
  /** How the caller relates to the class. */
  role: z.enum(['teacher', 'student']),
  /** Only for the teacher. */
  joinCode: z.string().nullable(),
  studentCount: z.number(),
  createdAt: z.string(),
});
export type ClassSummary = z.infer<typeof ClassSummary>;

export const ClassListResponse = z.object({ classes: z.array(ClassSummary) });
export type ClassListResponse = z.infer<typeof ClassListResponse>;

export const ClassStudent = z.object({
  id: z.string(),
  displayName: z.string(),
  /** Teachers see it; students never see each other's. */
  email: z.string().nullable(),
  joinedAt: z.string(),
});
export type ClassStudent = z.infer<typeof ClassStudent>;

export const ClassDetail = ClassSummary.extend({
  /** Empty for students. */
  students: z.array(ClassStudent),
});
export type ClassDetail = z.infer<typeof ClassDetail>;

export const CreateClassRequest = z.object({ name: ClassName });
export const UpdateClassRequest = z.object({ name: ClassName });

export const JoinPreview = z.object({
  classId: z.string(),
  className: z.string(),
  teacherName: z.string(),
  alreadyMember: z.boolean(),
});
export type JoinPreview = z.infer<typeof JoinPreview>;

/** An open live session broadcast to one of my classes (home page "Live now"). */
export const ClassLiveSession = z.object({
  viewCode: z.string(),
  classId: z.string(),
  className: z.string(),
  teacherName: z.string(),
  title: z.string(),
  startedAt: z.string(),
});
export type ClassLiveSession = z.infer<typeof ClassLiveSession>;

export const ClassLiveResponse = z.object({ sessions: z.array(ClassLiveSession) });
export type ClassLiveResponse = z.infer<typeof ClassLiveResponse>;

export const CreateSessionRequest = z.object({ classId: z.string().nullable().optional() });
export type CreateSessionRequest = z.infer<typeof CreateSessionRequest>;

// ——— Homework ———

export const AssignmentKind = z.enum(['exercise', 'custom', 'project']);
export type AssignmentKind = z.infer<typeof AssignmentKind>;

export const DueDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const AssignmentDto = z.object({
  id: z.string(),
  classId: z.string(),
  className: z.string(),
  kind: AssignmentKind,
  exerciseSlug: z.string().nullable(),
  title: z.string(),
  note: z.string(),
  dueDate: z.string().nullable(),
  createdAt: z.string(),
});
export type AssignmentDto = z.infer<typeof AssignmentDto>;

/**
 * todo: never opened · started: has a project · solved: the server checked every example ·
 * submitted: the student handed it in (a project assignment can only be submitted).
 */
export const WorkStatus = z.enum(['todo', 'started', 'solved', 'submitted']);
export type WorkStatus = z.infer<typeof WorkStatus>;

export const StudentAssignment = AssignmentDto.extend({
  status: WorkStatus,
  projectId: z.string().nullable(),
  submittedAt: z.string().nullable(),
  feedback: z.string().nullable(),
  feedbackAt: z.string().nullable(),
  late: z.boolean(),
});
export type StudentAssignment = z.infer<typeof StudentAssignment>;

export const HomeworkResponse = z.object({ assignments: z.array(StudentAssignment) });
export type HomeworkResponse = z.infer<typeof HomeworkResponse>;

export const TeacherAssignment = AssignmentDto.extend({
  students: z.number(),
  started: z.number(),
  done: z.number(),
});
export type TeacherAssignment = z.infer<typeof TeacherAssignment>;

export const ClassAssignmentsResponse = z.object({
  /** Teacher view: with progress counts. */
  teacher: z.array(TeacherAssignment).nullable(),
  /** Student view: with the student's own status. */
  student: z.array(StudentAssignment).nullable(),
});
export type ClassAssignmentsResponse = z.infer<typeof ClassAssignmentsResponse>;

export const CreateAssignmentRequest = z
  .object({
    /** A catalog slug or a `t-` slug of one of the teacher's exercises. */
    exerciseSlug: z.string().max(64).optional(),
    /** One of the teacher's projects, copied as the starting program. */
    projectId: z.string().max(64).optional(),
    title: z.string().trim().min(1).max(80).optional(),
    note: z.string().trim().max(1000).default(''),
    dueDate: DueDate.nullable().default(null),
  })
  .refine((b) => !!b.exerciseSlug !== !!b.projectId, {
    message: 'give either exerciseSlug or projectId',
  });
export type CreateAssignmentRequest = z.input<typeof CreateAssignmentRequest>;

export const UpdateAssignmentRequest = z.object({
  title: z.string().trim().min(1).max(80).optional(),
  note: z.string().trim().max(1000).optional(),
  dueDate: DueDate.nullable().optional(),
});
export type UpdateAssignmentRequest = z.infer<typeof UpdateAssignmentRequest>;

export const OpenAssignmentResponse = z.object({ projectId: z.string() });

export const WorkRow = z.object({
  userId: z.string(),
  displayName: z.string(),
  status: WorkStatus,
  projectId: z.string().nullable(),
  updatedAt: z.string().nullable(),
  submittedAt: z.string().nullable(),
  feedback: z.string().nullable(),
  late: z.boolean(),
});
export type WorkRow = z.infer<typeof WorkRow>;

export const AssignmentWorkResponse = z.object({
  assignment: AssignmentDto,
  rows: z.array(WorkRow),
});
export type AssignmentWorkResponse = z.infer<typeof AssignmentWorkResponse>;

export const FeedbackRequest = z.object({ text: z.string().trim().max(2000) });

// ——— Teacher-authored exercises ———

/** Custom exercise slugs; the catalog may not use this prefix. */
export const CUSTOM_PREFIX = 't-';
export const isCustomSlug = (slug: string) => slug.startsWith(CUSTOM_PREFIX);

export const CustomExample = ExerciseExample.extend({
  input: z.string().max(2000),
  output: z.string().max(2000),
  /** Hidden examples are checked on the server but never shown to the student. */
  hidden: z.boolean().default(false),
});
export type CustomExample = z.infer<typeof CustomExample>;

export const CustomExerciseInput = z
  .object({
    title: z.string().trim().min(1).max(80),
    summary: z.string().trim().max(160).default(''),
    statement: z.string().trim().min(1).max(4000),
    lang: UiLanguage,
    category: ExerciseCategory,
    difficulty: z.number().int().min(1).max(3),
    examples: z.array(CustomExample).min(1).max(20),
    hintBlocks: z.array(HintBlock).max(10).default([]),
    /** Copy the blocks of one of the author's projects as the starting program. */
    starterProjectId: z.string().max(64).nullable().optional(),
  })
  .refine((e) => e.examples.some((x) => !x.hidden), {
    message: 'at least one example must be visible',
    path: ['examples'],
  });
export type CustomExerciseInput = z.input<typeof CustomExerciseInput>;

export const CustomExerciseDto = z.object({
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  statement: z.string(),
  lang: UiLanguage,
  category: ExerciseCategory,
  difficulty: z.number(),
  examples: z.array(CustomExample),
  hintBlocks: z.array(HintBlock),
  hasStarter: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CustomExerciseDto = z.infer<typeof CustomExerciseDto>;

export const CustomExerciseListResponse = z.object({ exercises: z.array(CustomExerciseDto) });
export type CustomExerciseListResponse = z.infer<typeof CustomExerciseListResponse>;
